import { Validator } from '@vueform/vueform'

const normalizeValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value) && 'value' in value) {
    return value.value
  }

  return value
}

const getFieldValue = (form$, path) => {
  if (!path) {
    return undefined
  }

  const element$ = form$?.el$(path)

  if (!element$) {
    return undefined
  }

  return normalizeValue(element$.value)
}

const normalizeAttributes = (attributes) => {
  if (Array.isArray(attributes)) {
    return attributes
  }

  if (attributes && typeof attributes === 'object') {
    return Object.values(attributes)
  }

  return []
}

const buildValuesParam = (attributes) => {
  return normalizeAttributes(attributes)
    .filter((value) => value !== undefined && value !== null)
    .map((value) => String(value).trim())
    .filter(Boolean)
    .join(', ')
}

export class required_if extends Validator {
  get otherPath() {
    return normalizeAttributes(this.attributes)[0]
  }

  get expectedValue() {
    return normalizeAttributes(this.attributes)[1]
  }

  get other$() {
    return this.form$.el$(this.otherPath)
  }

  get messageParams() {
    return {
      attribute: this.attributeName,
      other: this.other$?.genericName,
      value: this.expectedValue,
    }
  }

  init() {
    if (this.otherPath) {
      this.watch(this.otherPath)
    }
  }

  isOtherFieldRequired() {
    const validationRules = this.other$?.validationRules

    if (Array.isArray(validationRules)) {
      return validationRules.includes('required') || validationRules.some((rule) => String(rule).startsWith('required:'))
    }

    if (typeof validationRules === 'string') {
      return validationRules.split('|').some((rule) => rule.trim() === 'required' || rule.trim().startsWith('required:'))
    }

    return false
  }

  check(value) {
    const otherValue = getFieldValue(this.form$, this.otherPath)

    if (this.expectedValue === undefined) {
      if (this.isOtherFieldRequired()) {
        return this.filled(value)
      }

      return true
    }

    if (String(otherValue) === String(this.expectedValue)) {
      return this.filled(value)
    }

    return true
  }
}

export class required_with extends Validator {
  get fields() {
    return normalizeAttributes(this.attributes).map((field) => String(field).trim()).filter(Boolean)
  }

  get messageParams() {
    return {
      attribute: this.attributeName,
      values: buildValuesParam(this.fields),
    }
  }

  init() {
    if (this.fields.length) {
      this.watch(this.fields)
    }
  }

  check(value) {
    const anyPresent = this.fields.some((field) => this.filled(getFieldValue(this.form$, field)))

    if (!anyPresent) {
      return true
    }

    return this.filled(value)
  }
}

export class required_with_all extends Validator {
  get fields() {
    return normalizeAttributes(this.attributes).map((field) => String(field).trim()).filter(Boolean)
  }

  get messageParams() {
    return {
      attribute: this.attributeName,
      values: buildValuesParam(this.fields),
    }
  }

  init() {
    if (this.fields.length) {
      this.watch(this.fields)
    }
  }

  check(value) {
    const allPresent = this.fields.length > 0 && this.fields.every((field) => this.filled(getFieldValue(this.form$, field)))

    if (!allPresent) {
      return true
    }

    return this.filled(value)
  }
}

export class required_without extends Validator {
  get fields() {
    return normalizeAttributes(this.attributes).map((field) => String(field).trim()).filter(Boolean)
  }

  get messageParams() {
    return {
      attribute: this.attributeName,
      values: buildValuesParam(this.fields),
    }
  }

  init() {
    if (this.fields.length) {
      this.watch(this.fields)
    }
  }

  check(value) {
    const anyMissing = this.fields.some((field) => !this.filled(getFieldValue(this.form$, field)))

    if (!anyMissing) {
      return true
    }

    return this.filled(value)
  }
}

export class required_without_all extends Validator {
  get fields() {
    return normalizeAttributes(this.attributes).map((field) => String(field).trim()).filter(Boolean)
  }

  get messageParams() {
    return {
      attribute: this.attributeName,
      values: buildValuesParam(this.fields),
    }
  }

  init() {
    if (this.fields.length) {
      this.watch(this.fields)
    }
  }

  check(value) {
    const nonePresent = this.fields.length > 0 && this.fields.every((field) => !this.filled(getFieldValue(this.form$, field)))

    if (!nonePresent) {
      return true
    }

    return this.filled(value)
  }
}
